import React from "react";
import { motion } from "framer-motion";
import {
  ArrowRight,
  Briefcase,
  Sparkles,
} from "lucide-react";

import AnimatedHeading from "../../../animations/AnimatedHeading";

const ApplyCTA = ({ job, onApply }) => {
  return (
    <section
      className="
        relative
        overflow-hidden
        bg-[var(--bg-primary)]
        pb-14
        font-saira
        sm:pb-16
        md:pb-20
      "
    >
      {/* ============================================================
          BACKGROUND GLOWS
      ============================================================ */}
      <div className="pointer-events-none absolute inset-0 overflow-hidden">
        {/* Center Glow */}
        <div
          className="
            absolute
            left-1/2
            top-1/2
            h-[260px]
            w-[260px]
            -translate-x-1/2
            -translate-y-1/2
            rounded-full
            bg-[color-mix(in_srgb,var(--brand-primary)_10%,transparent)]
            blur-[120px]
            sm:h-[350px]
            sm:w-[350px]
            sm:blur-[150px]
            lg:h-[450px]
            lg:w-[450px]
            lg:blur-[180px]
          "
        />

        {/* Left Glow */}
        <div
          className="
            absolute
            -left-20
            bottom-0
            h-48
            w-48
            rounded-full
            bg-[color-mix(in_srgb,var(--brand-primary)_5%,transparent)]
            blur-[100px]
            sm:-left-24
            sm:h-60
            sm:w-60
            sm:blur-[120px]
            lg:-left-32
            lg:h-72
            lg:w-72
            lg:blur-[150px]
          "
        />

        {/* Right Glow */}
        <div
          className="
            absolute
            -right-20
            top-0
            h-48
            w-48
            rounded-full
            bg-[color-mix(in_srgb,var(--brand-primary)_5%,transparent)]
            blur-[100px]
            sm:-right-24
            sm:h-60
            sm:w-60
            sm:blur-[120px]
            lg:-right-32
            lg:h-72
            lg:w-72
            lg:blur-[150px]
          "
        />
      </div>

      {/* ============================================================
          CONTAINER
      ============================================================ */}
      <div
        className="
          relative
          mx-auto
          w-full
          max-w-7xl
          px-4
          sm:px-6
          lg:px-8
        "
      >
        <motion.div
          initial={{ opacity: 0, y: 35 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="
            relative
            overflow-hidden
            rounded-[24px]
            border
            border-[var(--border-primary)]
            bg-[var(--card-bg)]
            shadow-[0_20px_60px_var(--shadow-color)]
            sm:rounded-[30px]
          "
        >
          {/* ============================================================
              SUBTLE CARD BACKGROUND
          ============================================================ */}
          <div
            className="
              pointer-events-none
              absolute
              inset-0
              bg-gradient-to-br
              from-[var(--card-bg)]
              via-[var(--card-bg-secondary)]
              to-[var(--bg-secondary)]
              opacity-90
            "
          />

          {/* ============================================================
              LEFT BRAND BORDER
          ============================================================ */}
          <div
            className="
              absolute
              left-0
              top-0
              h-full
              w-1
              bg-[var(--brand-primary)]
            "
          />

          {/* ============================================================
              CONTENT
          ============================================================ */}
          <div
            className="
              relative
              flex
              flex-col
              gap-8
              px-5
              py-8
              sm:gap-10
              sm:px-8
              sm:py-10
              md:px-10
              lg:flex-row
              lg:items-center
              lg:justify-between
              lg:px-14
              lg:py-14
            "
          >
            {/* ========================================================
                LEFT CONTENT
            ======================================================== */}
            <div className="w-full max-w-6xl">
              {/* Badge */}
              <div
                className="
                  inline-flex
                  items-center
                  gap-2
                  rounded-full
                  border
                  border-[color-mix(in_srgb,var(--brand-primary)_20%,transparent)]
                  bg-[color-mix(in_srgb,var(--brand-primary)_10%,transparent)]
                  px-4
                  py-2
                  text-xs
                  font-semibold
                  uppercase
                  tracking-[0.18em]
                  text-[var(--brand-light)]
                "
              >
                <Sparkles size={14} />

                <span>Join Our Team</span>
              </div>

              {/* Heading */}
              <div>
                <AnimatedHeading
                  title={`Become Our Next ${job.title}`}
                  highlight={job.title}
                />
              </div>

              {/* Description */}
              <p
                className="
                  text-base
                  leading-8
                  text-[var(--text-secondary)]
                  md:text-lg
                "
              >
                Join a passionate team where innovation, collaboration, and
                continuous learning drive everything we do. Work on meaningful
                digital products, solve real-world challenges, and build
                solutions that create lasting value while accelerating your
                professional growth.
              </p>

              {/* Highlights */}
              <div
                className="
                  mt-8
                  flex
                  flex-wrap
                  gap-x-6
                  gap-y-3
                  text-sm
                  text-[var(--text-muted)]
                "
              >
                <span>✓ Transparent Hiring Process</span>

                <span>✓ Growth-Oriented Culture</span>

                <span>✓ Equal Opportunity Workplace</span>
              </div>
            </div>

            {/* ========================================================
                RIGHT CTA
            ======================================================== */}
            <div
              className="
                flex
                w-full
                flex-col
                items-stretch
                lg:w-auto
                lg:min-w-[280px]
                lg:items-end
              "
            >
              <motion.button
                whileHover={{
                  scale: 1.03,
                  y: -2,
                }}
                whileTap={{
                  scale: 0.97,
                }}
                transition={{
                  duration: 0.25,
                }}
                onClick={() => onApply(job)}
                className="
                  group
                  flex
                  w-full
                  items-center
                  justify-center
                  gap-3
                  rounded-xl
                  bg-[var(--brand-primary)]
                  px-8
                  py-4
                  text-lg
                  font-semibold
                  text-white
                  transition-all
                  duration-300
                  hover:bg-[var(--brand-hover)]
                  hover:shadow-[0_20px_45px_color-mix(in_srgb,var(--brand-primary)_25%,transparent)]
                  sm:w-auto
                  sm:self-start
                  lg:self-auto
                "
              >
                <Briefcase
                  size={20}
                  className="
                    transition-transform
                    duration-300
                    group-hover:-rotate-6
                  "
                />

                <span>Apply Now</span>

                <ArrowRight
                  size={20}
                  className="
                    transition-transform
                    duration-300
                    group-hover:translate-x-1
                  "
                />
              </motion.button>
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default ApplyCTA;